/**
 * The **event-boundary cue registration** (Chassis Slice H, PRD #534 D7 /
 * ADR-0271): a new event's boundary audio is one {@link defineCue} line riding
 * the existing pure-{@link SoundEvent} pattern, instead of another hand-copied
 * `xCue.ts` module (this file folds the old `swarmCue.ts`/`stormCue.ts` twins).
 *
 * A boundary cue is the pure decision of *which* sound an event's
 * `active`/`inactive` status edge sounds - it fires off the event's status push
 * `active` flag flipping, never off a herald speech line: the status push is the
 * authoritative, exactly-once signal for both ends of the event, so it needs no
 * fragile text-matching against dynamic finale/winner lines. **The caller owns
 * the edge detection** - and since every installer's latch was itself a copied
 * twin, that discipline is folded here too as {@link createActiveEdge}: seed
 * silently on the first push of each connection (except that a first push that is
 * already `active` sounds the start cue - the server only seeds a status
 * mid-event, so a mid-event (re)login hears the arrival once, fitting for walking
 * into a bank mid-event), then act only on a real active<->inactive transition.
 *
 * Full volume by default, like {@link ./awardCue}: an event boundary is a klaxon
 * heard by everyone in the bank, not a sound positioned in the world. Playback
 * routes through {@link ./audioManager.AudioManager}, so the master mute
 * (settings panel) governs it.
 */
import type { SoundEvent, SoundId } from './soundObserver';

/** Event boundary cues are UI klaxons for everyone in the bank, not proximity-scaled. */
const EVENT_CUE_VOLUME = 1;

/** What an event's two status edges sound (registered sound ids, ADR-0092). */
export interface EventCueSpec {
  /** The sound for the `active: true` edge (the event's arrival). */
  active: SoundId;
  /** The sound for the `active: false` edge (the event's end). */
  inactive: SoundId;
  /** Loudness for both edges; defaults to the full-volume klaxon. */
  volume?: number;
}

/**
 * Register an event's boundary cue: returns the pure `active -> SoundEvent`
 * decision the installers play on a real status edge.
 */
export function defineCue(spec: EventCueSpec): (active: boolean) => SoundEvent {
  const volume = spec.volume ?? EVENT_CUE_VOLUME;
  return (active: boolean): SoundEvent => ({
    sound: active ? spec.active : spec.inactive,
    volume,
  });
}

/** The edge latch an installer drives its boundary cue (and siblings) through. */
export interface ActiveEdge {
  /** Forget the last-seen state - called on every (re)connection. */
  reset(): void;
  /**
   * Adopt a fresh push's `active` flag: returns whether this is a real
   * transition to act on (play the cue, flip the music override, ...). The
   * first push of a connection acts only if it is already `active` (the
   * mid-event login seed); a first-ever inactive push is silently seeded -
   * there is nothing to end.
   */
  observe(active: boolean): boolean;
}

/**
 * The shared edge discipline (seed silent, act only on real transitions) the
 * swarm and storm installers each hand-rolled as an `active: boolean | undefined`
 * latch. `undefined` until the first push of each connection.
 */
export function createActiveEdge(): ActiveEdge {
  let last: boolean | undefined;
  return {
    reset() {
      last = undefined;
    },
    observe(active: boolean): boolean {
      const crossed = active !== last && (last !== undefined || active);
      last = active;
      return crossed;
    },
  };
}

/**
 * The Rat Swarm boundary cue (ADR-0119): the flood-in scream when the swarm
 * becomes active, the "protect the bank" horn when the event is over.
 */
export const swarmCue = defineCue({ active: 'swarm-start', inactive: 'swarm-over' });

/**
 * The Blackthorn's Storm boundary cue (PRD #289 Slice E, ADR-0152): Blackthorn's
 * arrival growl on the `active: true` edge, and the shared mass-resurrection
 * chime on the `active: false` edge - the moment the storm breaks and Blackthorn
 * raises the fallen - one clear event-finale klaxon for everyone in the bank, on
 * top of the per-casualty proximity chimes the snapshot health-jump already
 * fires (ADR-0131).
 */
export const stormCue = defineCue({ active: 'storm-start', inactive: 'resurrect' });
